import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Form, InputGroup, Button, CloseButton, Alert, Row, Col, Image, Modal, Spinner } from "react-bootstrap";
import { fetchWithAuth, extractErrorMessage, formatPrice, sanitizeString } from "../../Functions";
import "../../assets/scss/components/custom-form.scss";

function AddListing() {
    const API = process.env.REACT_APP_API_BASE_URL;
    const initialFormState = {
        property_type: "",
        property_name: "",
        price: 0,
        description: "",
        state: "",
        city: "",
        land_use: "",
        land_title: "",
        bedrooms: null,
        bathrooms: null,
        terrace: null,
        land_size: null,
        features: [],
        newly_built: null,
        furnished: null,
        serviced: null,
        status: "",
        payment_plan: [],
        youtube_url: "",
    };
    const [selectedImages, setSelectedImages] = useState([]); // For new images selected via file input before form submission
    const [remoteImages, setRemoteImages] = useState([]); // For pre-existing images from remote backend
    const [previewImages, setPreviewImages] = useState([]); // For image previews both remote and local
    const [formState, setFormState] = useState(initialFormState);
    const [isLoading, setIsLoading] = useState(false); // For form submission
    const navigate = useNavigate();
    const [formFeedback, setFormFeedback] = useState({ success: null, message: "" });
    const [fetchFeedback, setFetchFeedback] = useState({ success: null, message: "" });

    // For property update
    const location = useLocation();
    const listingID = location?.state?.listingID;
    const [listing, setListing] = useState(null);
    const [images, setImages] = useState([]);
    const [imagesToDelete, setImagesToDelete] = useState([]);

    // Manage display of property price on form
    const [displayPrice, setDisplayPrice] = useState(formatPrice(listing?.price));

    // Pop up for max number of images exceeded
    const [showModal, setShowModal] = useState(false);
    const handleCloseModal = () => {
        setShowModal(false);
    }

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);

        // Check if total number of images exceeds limit
        if (files.length + selectedImages.length > 10) {
            setShowModal(true);
            return;
        }

        // Update selectedImages state with new files
        setSelectedImages((prevSelectedImages) => [...prevSelectedImages, ...files]);

        // Generate previews for new images
        files.forEach((file) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewImages((prevPreviews) => [...prevPreviews, reader.result]);
            }
            reader.readAsDataURL(file);
        });
    }

    const handleImageCancel = (indexToRemove) => {
        // Check if image at index is remote
        const isRemote = remoteImages.includes(previewImages[indexToRemove]);

        // Remove cancelled image from remote if it's within remote range
        if (isRemote) {
            // Get ID of cancelled image from images list
            const cancelledImageUrl = previewImages[indexToRemove];
            const cancelledImage = images.find(imgObj => imgObj.url === cancelledImageUrl);

            // Add ID of cancelled image to imagesToDelete for backend deletion
            if (cancelledImage) {
                setImagesToDelete(prev => [...prev, cancelledImage.id]);
            }

            const updatedRemote = remoteImages.filter((_, index) => index !== indexToRemove);
            setRemoteImages(updatedRemote);
        } else { // Remove cancelled image from local if it's within local range
            const updatedSelected = selectedImages.filter((_, index) => index !== indexToRemove);
            setSelectedImages(updatedSelected);
        }

        // Update previews whether in remote or local
        const updatedPreviews = previewImages.filter((_, index) => index !== indexToRemove);
        setPreviewImages(updatedPreviews);
    }

    const handleChange = (e) => {
        const { name, value, checked } = e.target;
        if (name === "features" || name === "payment_plan") {
            setFormState((prevState) => {
                const updated = [...prevState[name]]; // Create a copy of the features array
                if (checked) {
                  updated.push(value); // Add the selected feature if checked
                } else {
                  const index = updated.indexOf(value);
                  if (index !== -1) {
                    updated.splice(index, 1); // Remove the deselected feature
                  }
                }
                return { ...prevState, [name]: updated };
            });
        } else {
            setFormState((prevState) => ({
                ...prevState,
                [name]: value,
            }));
        }
    }

    // Handle change to price field
    const handlePriceChange = (e) => {
        const value = e.target.value;
        const sanitizedValue = sanitizeString(value);

        // Update the display with the formatted value
        setDisplayPrice(formatPrice(sanitizedValue));

        // Update actual formState with clean, numeric value
        setFormState((prevState) => ({
            ...prevState,
            price: sanitizedValue,
        }))
    }

    const fetchListing = useCallback(async (listingID) => {
        try {
            const response = await fetch(`${API}/properties/${listingID}/`);

            // Handle 401
            if (response.status === 401) {
                console.error("Session expired. Redirecting to admin login page.");

                // Set a message to inform the user before the redirect
                setFormFeedback({
                    success: false,
                    message: "Your session has expired. Please log in again."
                });

                // This is the line that performs the navigation
                navigate("/admin", { replace: true });

                // Stop further execution of the function
                return; 
            }

            // Handle other unsuccessful responses
            if (!response.ok) {
                const errorData = await response.json();
                setFetchFeedback({ success: false, message: "Unable to load properties, please refresh page."});
                console.error(errorData);
            }

            const listingData = await response.json();

            setListing(listingData);
            setFetchFeedback({ success: true, message: ""});
            return listingData;
        } catch (error) {
            setFetchFeedback({ success: false, message: "An unexpected error occured loading properties."});
            console.error(error);
        }
    }, [API, navigate]);

    // For converting property data into comparable state
    const getComparableState = (data) => {
        const { property_type, property_name, price, description, state, city, land_use, land_title, bedrooms, bathrooms, terrace, land_size, features, newly_built, furnished, serviced, status, payment_plan, youtube_url } = data;
        return { property_type, property_name, price, description, state, city, land_use, land_title, bedrooms, bathrooms, terrace, land_size, features, newly_built, furnished, serviced, status, payment_plan, youtube_url };
    }

    // Fetch property data if updating
    useEffect(() => {
        if (listingID) {
            fetchListing(listingID);
        }
    }, [listingID, fetchListing]);

    // Load property data into form if updating
    useEffect(() => {
        if (listing) {
            setFormState({
                property_name: listing.property_name,
                property_type: listing.property_type,
                land_title: listing.land_title,
                state: listing.state,
                city: listing.city,
                bedrooms: listing?.bedrooms,
                bathrooms: listing?.bathrooms,
                terrace: listing?.terrace,
                status: listing?.status,
                newly_built: listing?.newly_built,
                furnished: listing?.furnished,
                features: listing?.features,
                payment_plan: listing?.payment_plan,
                land_use: listing.land_use,
                land_size: listing?.land_size,
                serviced: listing.serviced,
                price: listing.price,
                description: listing.description,
                youtube_url: listing?.youtube_url,
            });

            setDisplayPrice(formatPrice(listing.price));

            const imageObjs = listing?.images.map(imgObj => ({
                "id": imgObj.id,
                "url": imgObj.image,
            }));

            setImages(imageObjs);
            const imageUrls = imageObjs.map(imgObj => imgObj.url);
            setRemoteImages(imageUrls);
            setPreviewImages(imageUrls);
        }
    }, [listing, API]);

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormFeedback({ success: null, message: "" }); // Reset form feedback

        // If UPDATE: Check that changes were detected before calling API
        if (listing) {
            const formComparableState = getComparableState(formState);
            const listingComparableState = getComparableState(listing);
            if (JSON.stringify(formComparableState) === JSON.stringify(listingComparableState)
                && imagesToDelete.length === 0
                && selectedImages.length === 0) {
                setFormFeedback({ success: false, message: "No changes detected." });
                return;
            }
        }

        setIsLoading(true);

        // console.log("Form state:\n", formState);

        // Create formData object
        const formData = new FormData();
        Object.keys(formState).forEach((key) => {
            if (!(formState[key])) {
                return;
            } else if (Array.isArray(formState[key])) {
                // Send arrays as JSON strings
                formData.append(key, JSON.stringify(formState[key]));
            } else if (key === "newly_built" || key === "furnished" || key === "serviced") {
                // Convert strings to boolean
                formData.append(key, formState[key] === "true");
            } else if (key === "price" || key === "bedrooms" || key === "terrace" || key === "land_size") {
                // Convert strings to number
                formData.append(key, parseInt(formState[key]));
            } else if (key === "bathrooms") {
                // Convert strings to float
                formData.append(key, parseFloat(formState[key]));
            } else {
                formData.append(key, formState[key]);
            }
        });

        // console.log("Form data");
        // for (const [key, value] of formData.entries()) {
        //     console.log(`${key} - ${value}`);
        // }

        // Append images to formData
        selectedImages.forEach((image) => formData.append("images", image));

        // Append IDs of removed images to formData to send along for deletion at backend
        if (listing) {
            imagesToDelete.forEach(imageID => formData.append("images_to_delete", imageID));
        }

        try {
            // Make API call
            const isEdit = Boolean(listing);
            const url = isEdit
                ? `${API}/properties/update/${listing.id}/`
                : `${API}/properties/create/`;
            const method = isEdit ? "PUT" : "POST";

            const response = await fetchWithAuth(url, {
                method: method,
                body: formData,
            });

            const data = await response.json();

            // Handle 401
            if (response.status === 401) {
                console.error("Session expired. Redirecting to admin login page.");

                // Set a message to inform the user before the redirect
                setFormFeedback({
                    success: false,
                    message: "Your session has expired. Please log in again."
                });

                // This is the line that performs the navigation
                navigate("/admin", { replace: true });

                // Stop further execution of the function
                return; 
            }

            // Handle successful response
            if (response.ok && data.status === "success") {
                setFormFeedback({
                    success: true,
                    message: isEdit ? "Listing successfully updated!" : "Listing successfully added!",
                });

                // Clear form on successful ADDITION of listing
                if (!isEdit) {
                    setFormState(initialFormState);
                    setDisplayPrice('');
                    setPreviewImages([]);
                }
                setSelectedImages([]);

                // Navigate to edit page for newly created property
                // if (!isEdit) {
                //     navigate("/dashboard/manage-listings/edit", { state: {listingID} });
                // }
                // return;
            } else {
                const defaultErrorMessage = `Unable to ${isEdit ? "update" : "add"} listing, please try again.`;
                setFormFeedback({
                    success: false,
                    message: extractErrorMessage(data) || defaultErrorMessage,
                });

                // Log error to console
                console.error(data);
            }
        } catch (error) {
            setFormFeedback({
                success: false,
                message: extractErrorMessage(error) || "An unexpected error has occured."
            });
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
        <h1 className="my-4 px-4 fw-semibold mb-4">{ listing ? "Update Listing" : "Add Listing" }</h1>

        {/* Fetch Listing Alert */}
        { fetchFeedback.success === false && // Display this alert only when fetchListings fails
            <Alert variant="danger" className="text-center">{ fetchFeedback.message }</Alert>
        }

        <p className="px-4 fs-6">{ listing ? "Edit" : "Fill" } the form below to { listing ? "update this" : "add a new" } listing.</p>
        <Form onSubmit={ handleSubmit } className="border dashboard-form bg-white px-4 pt-3 pb-3">
            {/* Name */}
            <Form.Group controlId="property-name" className="mb-4">
                <Form.Label>Property Name<small className="required text-danger fst-italic ms-1">&#40;required&#41;</small></Form.Label>
                <Form.Control
                    type="text"
                    name="property_name"
                    value={ formState.property_name }
                    onChange={ handleChange }
                    required
                />
            </Form.Group>

            {/* Type & Title */}
            <Row xs={1} md={2}>
                <Form.Group controlId="property-type" className="mb-4">
                    <Form.Label>Property Type<small className="required text-danger fst-italic ms-1">&#40;required&#41;</small></Form.Label>
                    <Form.Select name="property_type" value={ formState.property_type} onChange={ handleChange } required>
                        <option>Select</option>
                        <option value="Fully-Detached">Fully Detached</option>
                        <option value="Semi-Detached">Semi-Detached</option>
                        <option value="Terrace">Terrace Duplex</option>
                        <option value="Apartment">Apartment</option>
                        <option value="Bungalow">Bungalow</option>
                        <option value="Land">Land</option>
                        <option value="Office">Office</option>
                    </Form.Select>
                </Form.Group>
                <Form.Group controlId="land-title" className="mb-4">
                    <Form.Label>Title<small className="required text-danger fst-italic ms-1">&#40;required&#41;</small></Form.Label>
                    <Form.Control
                        type="text"
                        name="land_title"
                        value={ formState.land_title }
                        onChange={ handleChange }
                        autoComplete="off"
                        required
                    />
                </Form.Group>
            </Row>

            {/* State & City */}
            <Row xs={2}>
                <Form.Group controlId="state" className="mb-4">
                    <Form.Label>State<small className="required text-danger fst-italic ms-1">&#40;required&#41;</small></Form.Label>
                    <Form.Select name="state" value={ formState.state } onChange={ handleChange } required>
                        <option>Select</option>
                        <option value="Abuja">Abuja</option>
                        <option value="Lagos">Lagos</option>
                        <option value="Ogun">Ogun</option>
                        <option value="Oyo">Oyo</option>
                        <option value="Rivers">Rivers</option>
                        <option value="Others">Others</option>
                    </Form.Select>
                </Form.Group>
                <Form.Group controlId="city" className="mb-4">
                    <Form.Label>City<small className="required text-danger fst-italic ms-1">&#40;required&#41;</small></Form.Label>
                    <Form.Select name="city" value={ formState.city } onChange={ handleChange } required>
                        <option>Select</option>
                        <option value="Ajah">Ajah</option>
                        <option value="Chevron">Chevron</option>
                        <option value="Epe">Epe</option>
                        <option value="Ibadan">Ibadan</option>
                        <option value="Ibeju-Lekki">Ibeju-Lekki</option>
                        <option value="Lekki">Ikate-Lekki</option>
                        <option value="Ikota">Ikota</option>
                        <option value="Ikoyi">Ikoyi</option>
                        <option value="Lagos Mainland">Lagos Mainland</option>
                        <option value="Orchid">Orchid</option>
                        <option value="Port-Harcourt">Port-Harcourt</option>
                        <option value="Sangotedo">Sangotedo</option>
                        <option value="VGC">VGC</option>
                        <option value="Victoria Island">Victoria Island</option>
                        <option value="Wuse">Wuse</option>
                        <option value="Others">Others</option>
                    </Form.Select>
                </Form.Group>
            </Row>

            { (formState?.property_type && ( formState?.property_type !== "Land")) && 
                <>
                {/* Bedrooms & Bathrooms */}
                <Row xs={2}>
                    <Form.Group controlId="bedrooms" className="mb-4">
                        <Form.Label>Bedrooms</Form.Label>
                        <Form.Control
                            type="number"
                            min={1}
                            name="bedrooms"
                            value={ formState.bedrooms }
                            onChange={ handleChange }
                        />
                    </Form.Group>
                    <Form.Group controlId="bathrooms" className="mb-4">
                        <Form.Label>Bathrooms</Form.Label>
                        <Form.Control
                            type="number"
                            min={1}
                            step={0.5}
                            name="bathrooms"
                            value={ formState.bathrooms }
                            onChange={ handleChange }
                        />
                    </Form.Group>
                </Row>
                
                {/* Terraces & Status */}
                <Row xs={2}>
                    <Form.Group controlId="terraces" className="mb-4">
                        <Form.Label>Terrace Views</Form.Label>
                        <Form.Control
                            type="number"
                            min={0}
                            name="terrace"
                            value={ formState.terrace }
                            onChange={ handleChange }
                        />
                    </Form.Group>
                    <Form.Group controlId="house-status" className="mb-4">
                        <Form.Label>Current Status</Form.Label>
                        <Form.Select type="text" name="status" value={ formState.status } onChange={ handleChange }>
                            <option>Select</option>
                            <option value="OFFPLAN">Off Plan</option>
                            <option value="READYTOMOVEIN">Ready to Move In</option>
                        </Form.Select>
                    </Form.Group>
                </Row>

                {/* Newly built & Furnished */}
                <Row xs={2}>
                    <Form.Group controlId="build-status" className="mb-4">
                        <Form.Label>Newly Built</Form.Label>
                        <Form.Select name="newly_built" value={ formState.newly_built } onChange={ handleChange }>
                            <option>Select</option>
                            <option value={true}>Newly Built</option>
                            <option value={false}>Resale</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group controlId="furnished-status" className="mb-4">
                        <Form.Label>Delivery State</Form.Label>
                        <Form.Select name="furnished" value={ formState.furnished } onChange={ handleChange }>
                            <option>Select</option>
                            <option value={true}>Furnished</option>
                            <option value={false}>Finished</option>
                        </Form.Select>
                    </Form.Group>
                </Row>
                </>
            }

            {/* Features & Payment Plan*/}
            <Row md={2}>
                <Form.Group controlId="feature" className="mb-4">
                    <Form.Label>Features</Form.Label>
                    <InputGroup className="d-flex flex-column">
                        {[
                          "18-24hrs Power Supply",
                          "Secure & Gated Estate",
                          "Ample Parking Space",
                          "Astroturf",
                          "Box Room",
                          "Boys' Quarters",
                          "CCTV Cameras",
                          "Cinema",
                          "Elevator",
                          "Family Lounge",
                          "Fully Automated",
                          "Fully Fitted Kitchen",
                          "Gym",
                          "Kids' Play Area",
                          "Kitchen Island",
                          "Kitchen Pantry",
                          "Rooftop Terrace",
                          "Security House",
                          "Serviced Estate",
                          "Study",
                          "Swimming Pool",
                          "Terrace Views"
                        ].map((feature, index) => (
                            <Form.Check
                                key={ index }
                                type="checkbox"
                                id={`feature-${feature.replace(/\s+/g, '-')}`}
                                label={ feature }
                                name="features"
                                value={ feature }
                                checked={ formState.features.includes(feature) }
                                onChange={ handleChange }
                            />
                         ))}
                    </InputGroup>
                </Form.Group>
                <Form.Group controlId="payment-plan" className="mb-4">
                    <Form.Label>Payment Plans<small className="required text-danger fst-italic ms-1">&#40;required&#41;</small></Form.Label>
                    <InputGroup className="d-flex flex-column">
                        {["Outright", "3 Months", "6 Months", "9 Months", "12 Months", "18 Months", "24 Months"].map((plan, index) => (
                            <Form.Check
                                key={index}
                                type="checkbox"
                                id={`payment-plan-${plan.replace(/\s+/g, '-')}`}
                                label={ plan }
                                name="payment_plan"
                                value={ plan }
                                checked={ formState.payment_plan.includes(plan) }
                                onChange={ handleChange }
                            />
                        ))}
                    </InputGroup>
                </Form.Group>
            </Row>

            {/* Land Use & Land Size */}
            <Row xs={2}>
                <Form.Group controlId="land-use" className="mb-4">
                    <Form.Label>Land Use<small className="required text-danger fst-italic ms-1">&#40;required&#41;</small></Form.Label>
                    <Form.Select name="land_use" value={ formState.land_use } onChange={ handleChange } required>
                        <option>Select</option>
                        <option value="RESIDENTIAL">Residential</option>
                        <option value="COMMERCIAL">Commercial</option>
                        <option value="MIXED USE">Mixed Use</option>
                    </Form.Select>
                </Form.Group>
                <Form.Group controlId="land-size" className="mb-4">
                    <Form.Label>Land Size</Form.Label>
                    <Form.Control
                        type="number"
                        min={0}
                        name="land_size"
                        value={ formState.land_size }
                        onChange={ handleChange }
                    />
                </Form.Group>
            </Row>

            {/* Serviced & Price */}
            <Row xs={2}>
                <Form.Group controlId="serviced-status" className="mb-4">
                    <Form.Label>Serviced</Form.Label>
                    <Form.Select name="serviced" value={ formState.serviced } onChange={ handleChange }>
                        <option>Select</option>
                        <option value={true}>Fully serviced</option>
                        <option value={false}>Self serviced</option>
                    </Form.Select>
                </Form.Group>
                <Form.Group controlId="price" className="mb-4">
                    <Form.Label>Price<small className="required text-danger fst-italic ms-1">&#40;required&#41;</small></Form.Label>
                    <Form.Control
                        name="price"
                        value={ displayPrice }
                        onChange={ handlePriceChange }
                        required
                    />
                </Form.Group>
            </Row>

            {/* YouTube Link */}
            <Form.Group controlId="youtube-url" className="mb-4">
                <Form.Label>YouTube link</Form.Label>
                <Form.Control
                    type="url"
                    name="youtube_url"
                    value={ formState.youtube_url }
                    onChange={ handleChange }
                />
            </Form.Group>

            {/* Description */}
            <Form.Group controlId="description" className="mb-4">
                <Form.Label>Property Description<small className="required text-danger fst-italic ms-1">&#40;required&#41;</small></Form.Label>
                <Form.Control
                    as="textarea" rows={15}
                    name="description"
                    value={ formState.description }
                    onChange={ handleChange }
                    required
                />
            </Form.Group>

            {/* Images */}
            <Form.Group className="mb-4">
                <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={ handleImageChange }
                    id="custom-file-input"
                    className="d-none"
                    multiple
                />

                {/* Custom upload button for better user experience */}
                <InputGroup className="image-upload-btn d-flex align-items-center w-100 py-0">
                    <Button
                        className="d-inline rounded-0 border-0 border-end"
                        onClick={() => document.getElementById("custom-file-input").click()}
                    >
                        Choose Images
                    </Button>
                    <p className="m-0 py-0 px-3">
                        { selectedImages.length === 0
                            ? "No image chosen"
                            : `${selectedImages.length} images (max. 10)`
                        }
                    </p>
                </InputGroup>

                {/* Image preview */}
                <Row xs={3} sm={4} md={5} className="position-relative justify-content-start">
                    { previewImages &&
                        previewImages.map((image, index) => (
                            <Col key={ index } className="preview-image-container d-inline-flex position-relative mt-4 px-0">
                                <CloseButton
                                    onClick={() => handleImageCancel(index) }
                                    className="close-btn position-absolute end-0 rounded-5 bg-danger"
                                />
                                <Image src={ image } alt={`Image ${index + 1} preview`} className="preview-image mx-auto mt-4" rounded />
                            </Col>
                        ))
                    }
                </Row>

                {/* Popup warning when more than 10 images are attempted */}
                { showModal &&
                    <Modal show={ showModal } onHide={ handleCloseModal } size="sm" centered>
                        <Modal.Header closeVariant="danger" closeButton>
                            <Modal.Title as="h5">Warning</Modal.Title>
                        </Modal.Header>
                        <Modal.Body as="p" className="fw-semibold">You can only upload 10 images!</Modal.Body>
                    </Modal>
                }
            </Form.Group>

            <Button type="submit" className="px-3 px-md-5 mb-4">{ listing ? "Update Listing" : "Add Listing" }</Button>
            <br />
            { isLoading &&
                <div className="d-inline-block px-3 my-3">
                    <Spinner animation="border" className="d-flex align-self-center mx-auto" variant="primary" />
                    <p className="mt-2 text-center">{ listing ? "Updating listing" : "Creating listing"}...</p>
                </div>
            }

            { formFeedback.success != null &&
                <Alert variant={formFeedback.success ? "primary" : "danger"}>{ formFeedback.message }</Alert>
            }
        </Form>
        </>
    )
}

export default AddListing;
